// angular
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, effect, inject, OnDestroy, PLATFORM_ID } from '@angular/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { ChatHeaderComponent } from './components/chat-header/chat-header.component';
import { ChatMessagesContainerComponent } from './components/chat-messages-container/chat-messages-container.component';
// services
import { StateManagementService } from './shared/services/state-management.service';
import { LayoutService } from '@app/layout/shared/services/layout.service';
// types
import { MessagePayload } from 'firebase/messaging';

@Component({
  selector: 'pwj-chat-box',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ChatHeaderComponent,
    ChatMessagesContainerComponent,
  ],
  templateUrl: './chat-box.component.html',
  styleUrl: './chat-box.component.scss',
  host: { ngSkipHydration: 'true' },
})
export class ChatBoxComponent implements OnDestroy {
  public isChatOpened: boolean = false;
  public isMobileMenuOpen: boolean = false;
  public notifications!: MessagePayload[];

  // dependencies
  private readonly _stateManagementService = inject(StateManagementService);
  private readonly _layoutService = inject(LayoutService);
  private readonly _document = inject(DOCUMENT);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    // set chat box state
    effect(() => {
      this.isChatOpened = this._stateManagementService.isChatBoxOpen();
    });

    // set notifications data
    effect(() => {
      this.notifications = this._stateManagementService.messagesNotifications();
    });

    // TEMPORARY: chat server is unavailable, so chat data bootstrap is disabled.
    // effect(() => {
    //   this.currentChatId =
    //     this._stateManagementService.oneChatById()?._id || null;
    // });

    // hide floating chat UI while mobile nav layer is open
    effect(() => {
      this.isMobileMenuOpen = this._layoutService.mobileNavOpen();
    });

    // lock background page scroll while full-screen chat is open on mobile
    effect(() => {
      this.toggleBackgroundScrollLock(this.isChatOpened);
    });
  }

  public toggleChatBox() {
    // toggle chat box
    this._stateManagementService.isChatBoxOpen.set(true);
    // make notifications list empty
    this._stateManagementService.messagesNotifications.set([]);
  }

  private toggleBackgroundScrollLock(chatOpened: boolean) {
    if (!isPlatformBrowser(this._platformId)) return;

    const isMobileView = window.innerWidth <= 768;
    const shouldLock = chatOpened && isMobileView;

    this._document.body.style.overflow = shouldLock ? 'hidden' : '';
    this._document.body.style.touchAction = shouldLock ? 'none' : '';
    this._document.body.style.overscrollBehavior = shouldLock ? 'none' : '';
    this._document.documentElement.style.overflow = shouldLock ? 'hidden' : '';
  }

  ngOnDestroy(): void {
    this.toggleBackgroundScrollLock(false);
  }
}
